import * as Actions from 'actions'
import { Box } from '@material-ui/core'
import { setActivePreview, setPreviewLoading, setPreviewMode, toggleSplitPreview } from 'actions/preview'
import classNames from 'classnames'
import { PREVIEW_MODE, SPLIT_PREVIEW_BUTTONS } from 'config/constants/preview'
import { ViewSettings } from 'config/constants/editorSettings'
import { TranslationContext } from 'contexts/TranslationContext'
import { PLAYER_TYPE } from 'enums'
import React, { FC, MouseEvent, useCallback, useContext, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectorActivePreview } from 'selectors/preview'
import { selectViewSettings } from 'selectors/mainView'
import { PlayerType } from 'types/common'
import { PreviewModeType } from 'types/preview'
import { useAction } from 'hooks'
import { isRecordingStarted } from 'selectors/recording'
import { ReactComponent as Splitter } from '~/assets/preview/splitter.svg'
import { PreviewPlayerContext } from '../../contexts/PreviewPlayerContext'
import RawButton from '../base/RawButton/RawButton'
import { ReactComponent as CaptureIcon } from '~/assets/video-player/ic_capture.svg'

interface IProps {
 type: string,
 name?:string,
 togglePreview?(e: MouseEvent): void,
 showSplitter: boolean,
 playerType: PlayerType
 isSplitted: boolean,
 activeMode: PreviewModeType,
}

export const PreviewPlayerHead: FC<IProps> = ({ activeMode, type, name, showSplitter, playerType, isSplitted }) => {
  const activePreview = useSelector(selectorActivePreview)
  const mediaRecording = useSelector(isRecordingStarted)
  const viewSettings = useSelector(selectViewSettings)
  const stopRecordingAction = useAction(Actions.recording.stopRecording)
  const { t } = useContext(TranslationContext)
  const { captureFrame, imageSource, videoSource, canvasSource } = useContext(PreviewPlayerContext)
  const dispatch = useDispatch()
  const togglePreview = useCallback(() => {
    dispatch(toggleSplitPreview())
  }, [ dispatch ])

  const onSetPreview = useCallback((mode: PreviewModeType) => () => {
    dispatch(setPreviewMode(mode))
    if (mode !== PREVIEW_MODE.AUTO) {
      let preview
      switch (mode) {
        case PREVIEW_MODE.SOURCE:
          preview = PLAYER_TYPE.MEDIA
          break
        case PREVIEW_MODE.TIMELINE:
          preview = PLAYER_TYPE.TIMELINE
          break
      }
      if (preview !== activePreview) {
        dispatch(setPreviewLoading(true))
        dispatch(setActivePreview(preview))
      }
    }
  }, [ dispatch, activePreview ])

  const captureDisabled = useMemo(() => Boolean(PLAYER_TYPE.MEDIA === playerType
    ? (videoSource || imageSource)
    : canvasSource),
  [ playerType, imageSource, videoSource, canvasSource ])

  const capture = useCallback(() => {
    captureFrame(PLAYER_TYPE.MEDIA === playerType)
  }, [ captureFrame, playerType ])

  const onClockButtonBlock = () => {
    if (mediaRecording) {
      stopRecordingAction()
    }
  }

  return (
    <div className="head__item item" style={{ userSelect: 'none' }}>
      <Box
        display="flex"
        alignItems="center"
        gridGap="10px"
        className={classNames('item__box', { active: activePreview === playerType })}
      >
        <span className={classNames('item__type', { active: activePreview === playerType })}>
          {type}
        </span>
        <span className="item__name">{name || ''}</span>
      </Box>
      <div className="head__item__left" onClick={onClockButtonBlock}>
        {Boolean(viewSettings[ViewSettings.showPreviewSwitchButtons]) && (
          <Box className="item__preview-splitting preview-splitting">
            <Box className={classNames('preview-splitting__buttons button', { hidden: isSplitted })}>
              {SPLIT_PREVIEW_BUTTONS.map(({ name, mode }) => (
                <Box
                  className={classNames('button__item', { active: mode === activeMode })}
                  onClick={onSetPreview(mode as PreviewModeType)}
                  key={mode}
                >
                  {t(name)}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {(!isSplitted || showSplitter)
          && Boolean(viewSettings[ViewSettings.showPreviewSplitButton])
          && <Box onClick={togglePreview} className="preview-splitting__splitter"><Splitter /></Box>}

        {(__APP_PROFILE__ === 'public' || __APP_PROFILE__ === 'default') && (
          <div className="preview-frame-capture" title={t('PREVIEW_BTN_FRAME_CAPTURE_TOOLTIP')}>
            <RawButton
              dataLangId="PREVIEW_BTN_FRAME_CAPTURE"
              onClick={capture}
              disabled={!captureDisabled}
              ariaLabel={t('PREVIEW_BTN_FRAME_CAPTURE_TOOLTIP')}
            >
              <CaptureIcon />
            </RawButton>
          </div>
        )}
      </div>
    </div>
  )
}
